/**
 * Owner: Masa Bayu
 * File: hub-descriptor.ts
 * Date:Time: 2026-10-22 23:52
 * Description: Fasa 3 — descriptor qxp-hub → entri catalog serialized (overlay dinamik).
 *   §466 S1: metadata hanya medan sebenar dari manifest (tiada penulis/sinopsis rekaan).
 *
 * Hub = relay + auto-catalog; entri disintesis sebagai QxreadCatalogSerialized dengan
 * invariat authority utuh — catalog TIDAK PERNAH menggrants akses (contentAccess
 * 'not_granted_by_catalog'). Hak akses hanya melalui entitlement server (lihat hub-sync).
 */

import type { QxreadCatalogSerialized } from '@qx/qxread-core';

export interface QxpHubPublicationDescriptor {
  readonly publicationId: string;
  readonly qxpPublicationRef: string;
  readonly title: string;
  readonly languageRef: string;
  readonly packageId: string | null;
  readonly publishedAt: string;
  readonly artifactBaseUrl: string;
  /** Fasa 4 — harga/pengemasan daripada manifest terbitan (SSOT server). */
  readonly priceMinorUnits: number | null;
  readonly currency: string | null;
  readonly sampleMaxPage: number | null;
  readonly revision: number | null;
  /** §405 — taksonomi sebenar dari manifest; null bila terbitan tidak menandanya. */
  readonly category: string | null;
  readonly subcategory: string | null;
  readonly secondaryCategories: readonly string[];
  /** COVER-A3 — laluan relatif fail cover dalam bundle artifak (SSOT manifest). */
  readonly coverAssetRef: string | null;
  readonly coverSource: QxpHubCoverSource | null;
  /** Nama penerbit yang memiliki terbitan. Kosong bila akaun tiada nama paparan. */
  readonly authors: readonly string[];
}

/** Sumber cover seperti yang direkodkan pembundar: muat naik penerbit, atau lalai halaman 1. */
export type QxpHubCoverSource = 'uploaded' | 'page-underlay';

export interface QxpHubCatalog {
  readonly kind: string;
  readonly version: number;
  readonly updatedAt: string | null;
  readonly publications: readonly QxpHubPublicationDescriptor[];
}

const PUBLICATION_ID_PATTERN = /^qxrdpub_[a-z0-9]+(_[a-z0-9]+)*$/;
const REF_PATTERN = /^qxp_pub_[a-z0-9_]+$/;
const CURRENCY_PATTERN = /^[A-Z]{3}$/;
const SAFE_RELATIVE_PATH_PATTERN = /^[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)*$/;

function optionalIntAtLeast(value: unknown, min: number): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < min) return null;
  return value;
}

function optionalText(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/**
 * COVER-A3 — laluan cover ialah relatif selamat sahaja (fail-closed): mutlak,
 * `..`, atau aksara luar jadual → null (kad papar kotak nada, bukan URL karut).
 */
function optionalSafeRelativePath(value: unknown): string | null {
  const text = optionalText(value);
  if (text === null || !SAFE_RELATIVE_PATH_PATTERN.test(text)) return null;
  if (text.split('/').some((segment) => segment === '.' || segment === '..')) return null;
  return text;
}

function optionalCoverSource(value: unknown): QxpHubCoverSource | null {
  return value === 'uploaded' || value === 'page-underlay' ? value : null;
}

/** §405 — senarai tag taksonomi sebenar (main · sub · sekunder), bukan rekaan. */
function taxonomyTagsOf(
  category: string | null,
  subcategory: string | null,
  secondary: readonly string[],
): readonly string[] {
  const tags: string[] = [];
  if (category !== null) tags.push(category);
  if (subcategory !== null) tags.push(subcategory);
  for (const value of secondary) {
    if (!tags.includes(value)) tags.push(value);
  }
  return Object.freeze(tags);
}

/**
 * Medan harga/sampel Fasa 4 adalah opsyenal tetapi fail-closed: priceMinorUnits
 * diterima hanya dengan currency sah; nilai rosak dibuang (bukan menolak descriptor).
 */
export function readHubDescriptorCommerce(d: Record<string, unknown>): {
  readonly priceMinorUnits: number | null;
  readonly currency: string | null;
  readonly sampleMaxPage: number | null;
  readonly revision: number | null;
} {
  const priceRaw = optionalIntAtLeast(d.priceMinorUnits, 0);
  const currency =
    typeof d.currency === 'string' && CURRENCY_PATTERN.test(d.currency) ? d.currency : null;
  const priceMinorUnits = priceRaw !== null && currency !== null ? priceRaw : null;
  return Object.freeze({
    priceMinorUnits,
    currency: priceMinorUnits !== null ? currency : null,
    sampleMaxPage: optionalIntAtLeast(d.sampleMaxPage, 0),
    revision: optionalIntAtLeast(d.revision, 1),
  });
}

/**
 * COVER-A3 — pasangan (laluan, sumber) dibaca bersama; separuh sahaja tidak
 * diterima, kerana kad perlu tahu sama ada fail itu wujud dalam bundle.
 */
export function readHubDescriptorCover(d: Record<string, unknown>): {
  readonly coverAssetRef: string | null;
  readonly coverSource: QxpHubCoverSource | null;
} {
  const coverAssetRef = optionalSafeRelativePath(d.coverAssetRef);
  const coverSource = optionalCoverSource(d.coverSource);
  if (coverAssetRef === null || coverSource === null) {
    return Object.freeze({ coverAssetRef: null, coverSource: null });
  }
  return Object.freeze({ coverAssetRef, coverSource });
}

export function isQxpHubPublicationDescriptor(
  value: unknown,
): value is QxpHubPublicationDescriptor {
  if (value === null || typeof value !== 'object') return false;
  const d = value as Record<string, unknown>;
  return (
    typeof d.publicationId === 'string' &&
    PUBLICATION_ID_PATTERN.test(d.publicationId) &&
    typeof d.qxpPublicationRef === 'string' &&
    REF_PATTERN.test(d.qxpPublicationRef) &&
    typeof d.title === 'string' &&
    d.title.trim().length > 0 &&
    typeof d.languageRef === 'string' &&
    typeof d.publishedAt === 'string' &&
    typeof d.artifactBaseUrl === 'string' &&
    d.artifactBaseUrl.startsWith('/cdn/qxp/v1/packages/')
  );
}

export function parseQxpHubPublicationDescriptor(
  value: unknown,
): QxpHubPublicationDescriptor | null {
  if (!isQxpHubPublicationDescriptor(value)) return null;
  const d = value as unknown as Record<string, unknown>;
  const category = optionalText(d.category);
  const subcategory = optionalText(d.subcategory);
  const secondary = Array.isArray(d.secondaryCategories)
    ? d.secondaryCategories
        .map((item) => optionalText(item))
        .filter((item): item is string => item !== null)
    : [];
  const authors = Array.isArray(d.authors)
    ? d.authors
        .map((item) => optionalText(item))
        .filter((item): item is string => item !== null)
    : [];
  return Object.freeze({
    publicationId: d.publicationId as string,
    qxpPublicationRef: d.qxpPublicationRef as string,
    title: (d.title as string).trim(),
    languageRef: d.languageRef as string,
    packageId: typeof d.packageId === 'string' ? d.packageId : null,
    publishedAt: d.publishedAt as string,
    artifactBaseUrl: d.artifactBaseUrl as string,
    ...readHubDescriptorCommerce(d),
    ...readHubDescriptorCover(d),
    category,
    subcategory,
    secondaryCategories: Object.freeze(secondary),
    authors: Object.freeze(authors),
  });
}

export function parseQxpHubCatalog(body: unknown): QxpHubCatalog | null {
  if (body === null || typeof body !== 'object') return null;
  const c = body as Record<string, unknown>;
  if (c.kind !== 'qxp_hub_catalog' || !Array.isArray(c.publications)) return null;
  const publications = c.publications
    .map(parseQxpHubPublicationDescriptor)
    .filter((d): d is QxpHubPublicationDescriptor => d !== null);
  return Object.freeze({
    kind: 'qxp_hub_catalog',
    version: typeof c.version === 'number' ? c.version : 1,
    updatedAt: typeof c.updatedAt === 'string' ? c.updatedAt : null,
    publications: Object.freeze(publications),
  });
}

/**
 * Sintesis entri catalog daripada descriptor hub. Semua medan lifecycle adalah
 * nilai tetap 'live' — sumber kebenaran artifak/sendiri berada di artifak QXP
 * yang melalui pintu integriti reader; catalog hanya permukaan rujukan.
 */
export function hubDescriptorToCatalogSerialized(
  descriptor: QxpHubPublicationDescriptor,
): QxreadCatalogSerialized {
  return Object.freeze({
    publicationId: descriptor.publicationId,
    qxpPublicationRef: descriptor.qxpPublicationRef,
    title: descriptor.title,
    visibility: 'public',
    refState: 'bound',
    listedVersion: null,
    listingState: 'live',
    availability: 'available',
    marketplaceReady: true,
    metadata: Object.freeze({
      // §422 K1 — title dalam metadata: reader membaca entry.metadata.title;
      // tanpa ini buku hub paparkan 'Untitled'.
      title: descriptor.title,
      authors: Object.freeze([...descriptor.authors]),
      language: descriptor.languageRef,
      description: null,
      // COVER-A3 — laluan relatif fail cover dalam bundle (manifest hub); kad
      // compos URL melalui hub-covers, bukan membaca medan ini sebagai URL.
      coverAssetRef: descriptor.coverAssetRef,
      // Taksonomi sebenar manifest §405 (main · sub · sekunder); kosong bila
      // terbitan tidak ditanda kategorinya.
      tags: taxonomyTagsOf(
        descriptor.category,
        descriptor.subcategory,
        descriptor.secondaryCategories,
      ),
      publishedAt: descriptor.publishedAt,
    }),
    version: null,
    preview: null,
    authority: Object.freeze({
      publicationTruth: 'qxp',
      catalogRole: 'reference',
      contentAccess: 'not_granted_by_catalog',
    }),
  });
}
